/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbBooleanLogicSingleConditionContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicSingleConditionContent";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { NullUtils } from "abweb/utils/NullUtils";
import { List, Record } from "immutable";
const AbBooleanLogicGroupingContentDefaults = {
  key: "",
  cardinality: "GROUP",
  connective: "AND",
  sequence: List(),
  disabled: false
};
export class AbBooleanLogicGroupingContent extends Record(AbBooleanLogicGroupingContentDefaults) {
  constructor(key, connective) {
    super({
      key,
      connective
    });
  }
  // helper method for strong typing
  with(values) {
    return this.merge(values);
  }
  visitAllNodes(visit) {
    visit(this);
    this.sequence.toArray().map(item => {
      if (item.cardinality === "SINGLE") {
        visit(item);
      } else {
        item.visitAllNodes(visit);
      }
    });
  }
  getOppositeConnective() {
    return this.connective === "AND" ? "OR" : "AND";
  }
  withConnectiveToggled() {
    return this.with({
      connective: this.getOppositeConnective()
    });
  }
  withConnective(value) {
    return this.with({
      connective: value
    });
  }
  withDisabled(value) {
    return this.with({
      disabled: value
    });
  }
  withSequence(sequence) {
    return this.with({
      sequence: sequence
    });
  }
  withAppendSingleRow(item) {
    return this.with({
      sequence: this.sequence.push(item)
    });
  }
  withAppendSingleRowWithDisableData(item, disabled) {
    return this.with({
      sequence: this.sequence.push(item.withDisabled(disabled))
    });
  }
  withAppendSubgroup(item) {
    return this.with({
      sequence: this.sequence.push(item)
    });
  }
  withItemRemovedAt(index) {
    return this.with({
      sequence: this.sequence.delete(index)
    });
  }
  withSingleItemSetAt(index, item) {
    return this.with({
      sequence: this.sequence.set(index, item)
    });
  }
  withGroupItemSetAt(index, item) {
    return this.with({
      sequence: this.sequence.set(index, item)
    });
  }
  withItemInsertedAt(index, item) {
    return this.with({
      sequence: this.sequence.insert(index, item)
    });
  }

  /**
   * Return a new group with the provided subgroup inserted at the specified index
   */
  withSubgroupInsertedAt(index, subgroup) {
    return this.with({
      sequence: this.sequence.insert(index, subgroup)
    });
  }
  withItemMoveUp(index) {
    if (index <= 0) {
      return this;
    } // no-op

    const newSequence = this.sequence.remove(index).insert(index - 1, this.sequence.get(index));
    return this.with({
      sequence: newSequence
    });
  }
  withItemMoveDown(index) {
    if (index >= this.sequence.count() - 1) {
      return this;
    } // no-op

    const newSequence = this.sequence.remove(index).insert(index + 1, this.sequence.get(index));
    return this.with({
      sequence: newSequence
    });
  }

  /**
   * Return a new group with the condition that is at the specified index duplicated immediately after
   */
  withDuplicateCondition(index, newConditionKey) {
    const newRow = new AbBooleanLogicSingleConditionContent(newConditionKey);
    const newSequence = this.sequence.insert(index + 1, newRow);
    return this.with({
      sequence: newSequence
    });
  }

  /**
   * Take an item by index and enclose it (indent it) in a subgroup.
   */
  withItemSubGrouped(index, groupKey) {
    const row = this.sequence.get(index);
    const newGroup = new AbBooleanLogicGroupingContent(groupKey, this.connective === "AND" ? "OR" : "AND").withSequence(List([row]));
    const immSequence = this.sequence.set(index, newGroup);
    return this.withSequence(immSequence);
  }

  /**
   * Get an item by index and return it in a type safe way.
   */
  getItem(index) {
    return this.sequence.get(index);
  }

  /**
   * Get the number of items in the condition group
   */
  getItemCount() {
    return this.sequence.size;
  }

  /**
   * Take an item by index and enclose it (indent it) in a subgroup.
   */
  withSubGroupUnfolded(index) {
    const subgroup = this.sequence.get(index);
    if (subgroup.cardinality !== "GROUP") {
      return Assert.unexpected();
    }

    // TODO - I think this might not be the optimal way to splice a list into another with immutablejs
    const immSeq = this.sequence.splice(index, 1, ...subgroup.sequence.toArray()).toList();
    return this.withSequence(immSeq);
  }

  // private containsRowIdKeyThisLevel(rowKey: string):boolean
  withRowMoved(fromIndex, toIndex) {
    if (fromIndex === toIndex || fromIndex + 1 === toIndex) {
      console.log(`skipping row move from ${fromIndex} to ${toIndex}`); //`
      return this; // non-move, so no change
    }
    const elem = this.sequence.get(fromIndex);
    if (fromIndex < toIndex) {
      const newSequence = this.sequence.delete(fromIndex).insert(toIndex - 1, elem);
      return this.withSequence(newSequence);
    }
    if (toIndex < fromIndex) {
      const newSequence = this.sequence.delete(fromIndex).insert(toIndex, elem);
      return this.withSequence(newSequence);
    }
    Assert.unexpected();
    return this;
  }
  moveRowByKey(rowKey, destinationInfo) {
    // Look for a move where the source and dest are within the same group sequence
    // handle this case specially, and handle that specially
    if (destinationInfo.parentGroup === this) {
      const removeIndex = this.sequence.findIndex(value => {
        return value?.key === rowKey;
      });
      if (removeIndex !== -1) {
        console.log(`within row move from ${removeIndex} to ${destinationInfo.index}`); //`
        return this.withRowMoved(removeIndex, destinationInfo.index);
      }
    }

    // The Map function can be used to recursively remove the old element,
    // and insert the new one at the correct place.
    const newSequence = this.sequence.toSeq().filter(value => {
      // filter to remove the row from the old location
      if (value?.key === rowKey) {
        console.log(`removing row ${rowKey} in group ${this.key}`); //`
        return false;
      } else {
        return true;
      }
    }).map(value => {
      // within the map function, recurse into the group structure
      if (value?.cardinality === "GROUP") {
        return value.moveRowByKey(rowKey, destinationInfo);
      } else {
        return value;
      }
    }).filter(value => {
      // It is possible that moving the row caused a group to be empty
      // If this happens, we also want to remove the group
      if (ClassUtils.isInstanceOf(value, AbBooleanLogicSingleConditionContent)) {
        return true;
      }
      if (ClassUtils.isInstanceOf(value, AbBooleanLogicGroupingContent)) {
        const group = value;
        return group.sequence.size > 0;
      }
      return false;
    }).toList();
    let ret = this.withSequence(newSequence);
    if (destinationInfo.parentGroup === this) {
      ret = ret.withItemInsertedAt(destinationInfo.index, new AbBooleanLogicSingleConditionContent(rowKey));
    }
    return ret;
  }
  removeRowsByKey(rowKeys) {
    const newSequence = this.sequence.toSeq().filter(value => {
      if (value?.key && rowKeys.includes(value.key)) {
        return false;
      } else {
        return true;
      }
    }).map(value => {
      if (value?.cardinality === "GROUP") {
        return value.removeRowsByKey(rowKeys);
      } else {
        return value;
      }
    }).filter(value => {
      if (ClassUtils.isInstanceOf(value, AbBooleanLogicSingleConditionContent)) {
        return true;
      }
      if (ClassUtils.isInstanceOf(value, AbBooleanLogicGroupingContent)) {
        const group = value;
        return group.sequence.size > 0;
      }
      return false;
    }).toList();
    let ret = this.withSequence(newSequence);
    return ret;
  }
  insertRowsAtTreeLocation(dropTargetLocation, rowsToInsert) {
    const newSequence = this.sequence.toSeq().map(value => {
      // within the map function, recurse into the group structure
      if (value?.cardinality === "GROUP") {
        return value.insertRowsAtTreeLocation(dropTargetLocation, rowsToInsert);
      } else {
        return value;
      }
    }).toList();
    let ret = this.withSequence(newSequence);
    if (dropTargetLocation.parentGroup.key === this.key) {
      for (let i = 0; i < rowsToInsert.length; i++) {
        ret = ret.withItemInsertedAt(dropTargetLocation.index + i, rowsToInsert[i]);
      }
    }
    return ret;
  }
  getRowsByKey(rowKeys) {
    const newSequence = this.sequence.toSeq().map(value => {
      if (value?.cardinality === "GROUP") {
        return value.getRowsByKey(rowKeys);
      } else {
        return value;
      }
    }).filter(value => {
      if (ClassUtils.isInstanceOf(value, AbBooleanLogicSingleConditionContent) && rowKeys.includes(value.key)) {
        return true;
      }
      if (ClassUtils.isInstanceOf(value, AbBooleanLogicGroupingContent)) {
        const group = value;
        return group.sequence.size > 0;
      }
      return false;
    }).toList();
    let ret = this.withSequence(newSequence);
    return ret;
  }
  performConditionalActionOnRows(conditionAction, conditionalCheck = () => true) {
    const newSequence = this.sequence.toSeq().map(value => {
      // within the map function, recurse into the group structure
      let rowOrGroup = NullUtils.assertDefined(value);
      if (ClassUtils.isInstanceOf(rowOrGroup, AbBooleanLogicGroupingContent)) {
        return rowOrGroup.performConditionalActionOnRows(conditionAction, conditionalCheck);
      } else if (ClassUtils.isInstanceOf(rowOrGroup, AbBooleanLogicSingleConditionContent) && conditionalCheck(rowOrGroup)) {
        return conditionAction(rowOrGroup);
      }
      return rowOrGroup;
    }).toList();
    let ret = this.withSequence(newSequence);
    return ret;
  }
  static analyzeSelection(treeLogic, selectionMachine) {
    // Local Analysis
    let anySelected = false;
    let isContiguous = true;
    let allSelected = true;

    //Nested Analysis
    let containsAtLeastOneLocalSelection = false;
    let numberOfFullySelectedSubgroups = 0;
    let containsOneValidChild = false;
    let seenValidChild = false;
    let foundStartOfSelection = false;
    let foundEndOfSelection = false;
    for (let index = 0; index < treeLogic.sequence.size; index++) {
      let item = treeLogic.sequence.get(index);
      if (ClassUtils.isInstanceOf(item, AbBooleanLogicSingleConditionContent)) {
        // Local Validity Analysis
        if (!selectionMachine.isSelected(item.key)) {
          allSelected = false;
        }
        if (selectionMachine.isSelected(item.key)) {
          containsAtLeastOneLocalSelection = true;
          anySelected = true;
        }
        if (!foundStartOfSelection && selectionMachine.isSelected(item.key)) {
          foundStartOfSelection = true;
        } else if (foundStartOfSelection && !selectionMachine.isSelected(item.key)) {
          foundEndOfSelection = false;
        }
        if (foundEndOfSelection && selectionMachine.isSelected(item.key)) {
          isContiguous = false;
        }
      }
      if (ClassUtils.isInstanceOf(item, AbBooleanLogicGroupingContent)) {
        let ret = this.analyzeSelection(item, selectionMachine);
        // Nested Validity Analysis
        if (ret.isValidForIndent && seenValidChild) {
          containsOneValidChild = false;
        } else if (ret.isValidForIndent && !seenValidChild) {
          containsOneValidChild = true;
          seenValidChild = true;
        }
        // Local Validity Analysis
        if (ret.allSelected) {
          numberOfFullySelectedSubgroups++;
        }
        if (ret.containsSelection && !ret.allSelected) {
          isContiguous = false;
        }
        if (!ret.allSelected) {
          allSelected = false;
        }
        if (ret.containsSelection) {
          anySelected = true;
        }
        if (!foundStartOfSelection && ret.allSelected) {
          foundStartOfSelection = true;
        } else if (foundStartOfSelection && !ret.allSelected) {
          foundEndOfSelection = true;
        }
        if (foundEndOfSelection && ret.allSelected) {
          isContiguous = false;
        }
      }
    }
    let isValidForIndentLocally = isContiguous && (containsAtLeastOneLocalSelection || numberOfFullySelectedSubgroups > 1) && !allSelected;
    let isValidForIndentNested = containsOneValidChild && !containsAtLeastOneLocalSelection;
    return {
      containsSelection: anySelected,
      allSelected: allSelected,
      isValidForIndent: isValidForIndentLocally || isValidForIndentNested
    };
  }

  // Selects range of conditions between previous in-focus condition and the current in-focus condition
  static getItemRange(treeLogic, selectionMachine, targetKey, currentlyAdding = false) {
    let selectionList = [];
    for (let index = 0; index < treeLogic.sequence.size; index++) {
      let item = treeLogic.sequence.get(index);
      if (!currentlyAdding && item.key === targetKey) {
        currentlyAdding = true;
        targetKey = selectionMachine.focusedSelection;
      } else if (!currentlyAdding && selectionMachine.isFocusedSelection(item.key)) {
        currentlyAdding = true;
      }
      if (ClassUtils.isInstanceOf(item, AbBooleanLogicSingleConditionContent)) {
        if (currentlyAdding) {
          selectionList.push(item.key);
        }
        if (item.key === targetKey) {
          return {
            selectionList,
            targetKey,
            currentlyAdding
          };
        }
      } else if (ClassUtils.isInstanceOf(item, AbBooleanLogicGroupingContent)) {
        let nestedRet = AbBooleanLogicGroupingContent.getItemRange(item, selectionMachine, targetKey, currentlyAdding);
        selectionList = selectionList.concat(nestedRet.selectionList);
        targetKey = nestedRet.targetKey;
        currentlyAdding = nestedRet.currentlyAdding;
        if (selectionList.includes(targetKey)) {
          // If recursive call has found target, we do not want to continue
          return {
            selectionList,
            targetKey,
            currentlyAdding
          };
        }
      }
    }
    return {
      selectionList,
      targetKey,
      currentlyAdding
    };
  }
  findRowOrGroupLocation(key) {
    for (let i = 0; i < this.sequence.size; i++) {
      const rowOrGroup = this.sequence.get(i);
      if (rowOrGroup.cardinality === "SINGLE") {
        if (rowOrGroup.key === key) {
          return {
            parentGroup: this,
            index: i,
            isGroup: false
          };
        }
      } else if (rowOrGroup.cardinality === "GROUP") {
        if (rowOrGroup.key === key) {
          return {
            parentGroup: this,
            index: i,
            isGroup: true
          };
        }
        const subGroupInfo = rowOrGroup.findRowOrGroupLocation(key);
        if (subGroupInfo) {
          return subGroupInfo;
        }
      }
    }
    return null;
  }
  findRowLocation(rowKey) {
    for (let i = 0; i < this.sequence.size; i++) {
      const rowOrGroup = this.sequence.get(i);
      if (rowOrGroup.cardinality === "SINGLE") {
        if (rowOrGroup.key === rowKey) {
          return {
            parentGroup: this,
            index: i,
            isGroup: false
          };
        }
      } else {
        const subGroupInfo = rowOrGroup.findRowLocation(rowKey);
        if (subGroupInfo) {
          return subGroupInfo;
        }
      }
    }
    return null;
  }
}
//# sourceMappingURL=AbBooleanLogicGroupingContent.js.map
